import { PerformanceCoreDatapoint } from "owa-analytics";
import { type CustomData, type CustomWaterfallRange, DatapointStatus } from "owa-analytics-types";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import type { IUTEPlacesMapsLoadE2EArgs } from "@outlook/unified_telemetry";
import { PlacesMarkerTimeout } from "./constants";
import { actActionIfExecuting } from "./utils/actActionIfExecuting";
import { addCustomWaterfallIfExecuting } from "./utils/addCustomWaterfallIfExecuting";
import { createPlacesPerformanceMarker } from "./utils/createPlacesPerformanceMarker";
import { isExecuting } from "./utils/isExecuting";

/**
 * These strings are expensive to store in kusto. keeping it short.
 * This list is the source of truth.
 */
export const STEP_TO_WATERFALL_INDEX_MAP: {
    [checkpoint: string]: CustomWaterfallRange;
} = {
    idbe: 1, // IndexedDB fetch ends
    gmfe: 2, // GetMapFeatures request ends (if network request is made)
    mcs: 3, // Map container starts to load
    mi: 4, // Map initialized
    bla: 5, // Map base layers added
    fla: 6, // Map fixture layers added
    img: 7, // Map images added
    sla: 8, // Map symbol layers added
    idle: 9, // Map becomes idle
    prevcomplete: 10, // Map previous complete checkpoint for renderV2
};

/**
 * Measures E2E latency and latency breakdown for loading a map in Places Finder.
 * A performanceDatapoint is started when a new unit is selected in finder, and ends when the map finishes
 * loading all layers and markers.
 */
let PlacesMapsLoadE2E: PerformanceCoreDatapoint | undefined = undefined;
let prevUnitId: string | undefined = undefined;
let customProperties: {
    [key: string]: any;
} = {};
let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

export function PlacesMapsLoadE2EStart(unitId: string | undefined, skip: boolean = true): void {
    if (!unitId || (skip && unitId === prevUnitId)) {
        return;
    }
    // Clear custom properties
    customProperties = {};
    
    // If a new data point is initiated before the curent one ends
    // (user selects a new unit before the current map finishes loading),
    // cancel the previous datapoint and proceed with the new one.
    if (isExecuting(PlacesMapsLoadE2E)) {
        PlacesMapsLoadE2E.invalidate();
        PlacesMapsLoadE2E = undefined;
    }
    
    // Clear any existing timeout to prevent stale timeouts
    if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
    }
    
    const dp = new PerformanceCoreDatapoint("PlacesMapsLoadE2E", getModuleContextMailboxInfo(), {
        // slightly increase the datapoint's timeout to make sure our setTimeout is executed before default timeout so UT is logged
        timeout: PlacesMarkerTimeout + 10000,
    });
    
    timeoutHandle = setTimeout(() => {
        // Collect additional context information for timeout scenarios
        const timeoutContext = {
            visibilityState: document.visibilityState,
            hasFocus: document.hasFocus(),
        };
        
        PlacesMapsLoadE2EComplete(false, DatapointStatus.Timeout, JSON.stringify(timeoutContext));
        timeoutHandle = undefined;
    }, PlacesMarkerTimeout);
    
    PlacesMapsLoadE2E = createPlacesPerformanceMarker(dp);
    prevUnitId = unitId;
}

export function PlacesMapsLoadE2EComplete(loadSuccessful: boolean, errorStatus?: DatapointStatus, reason?: string): void {
    if (!isExecuting(PlacesMapsLoadE2E)) {
        return;
    }
    
    // Clear the timeout since we're ending manually
    if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
    }
    
    if (reason) {
        customProperties["ActionFailureInfo"] = reason;
    }
    PlacesMapsLoadE2E?.addUnifiedTelemetryData({
        eventName: "PlacesMapsLoadE2E",
        data: customProperties as IUTEPlacesMapsLoadE2EArgs,
    });
    actActionIfExecuting(PlacesMapsLoadE2E, customProperties, loadSuccessful ? "end" : "endWithError" /* action */, reason ? Error(reason) : undefined /* errorObject */, errorStatus);
    
    PlacesMapsLoadE2E = undefined;
}

export function PlacesMapsLoadE2ECancel(reason = ""): void {
    actActionIfExecuting(PlacesMapsLoadE2E, {
        reason,
    }, "invalidate" /* action */);
}

export const PlacesMapsLoadE2ECheckmark = (checkpoint: keyof typeof STEP_TO_WATERFALL_INDEX_MAP): void => {
    addCustomWaterfallIfExecuting(PlacesMapsLoadE2E, STEP_TO_WATERFALL_INDEX_MAP[checkpoint], checkpoint as string);
};

export const PlacesMapsLoadE2ECustomData = (customData: CustomData): void => {
    customProperties = { ...customProperties, ...customData };
    actActionIfExecuting(PlacesMapsLoadE2E, customData);
};
